import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { Field, type FieldAttributes, useFormikContext } from 'formik';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

import type { NestedKeyOf } from '@core/utils/types';
import type { YupSchemaReferenceType } from '@core/utils/yupHelpers';

import {
  AlertDupeOptions,
  GetOptFromValue,
  LookupInputAutocompleteOption,
  LookupInputInput,
  type LookupInputOption,
  type SharedLookupInputProps,
} from './LookupInputSharedComponents';

// Typing in the autocomplete box will match against the label or the sublabel
const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: LookupInputOption) => `${option.label}${(option.sublabels ?? []).join('')}`,
});

/**
 * A Slabstack formik-friendly datalist.
 *
 * Must be used inside `<Formik><Form>{component}</Form></Formik>`.
 *
 * @example
 * <Formik {your_values_here} >
 * {(formikBag): ReactElement => (
 *   <Form>
 *     <LocalLookupInput
 *       label='Company name'
 *       name='company.id'
 *       options={companyOpts}
 *       onMatchChange={(companyID: string | null): void => {
 *         // InputLookup has empty text (no match)
 *         if (companyID === null) {}
 *         // InputLookup has text that matches a value
 *         if (companyID !== null) {}
 *       }}
 *     />
 *   </Form>
 * </Formik>
 */
export const LocalLookupInput = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
>({
  label,
  name,
  options,
  grouped = false,
  onMatchChange,
  placeholder,
  disabled,
  tip,
  sx,
  readOnly,
  validate,
  isLoading = false,
}: SharedLookupInputProps<FState, FPath>): ReactElement => {
  AlertDupeOptions(options);
  const labelsByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);

  const { getFieldMeta } = useFormikContext();
  const formikObject = getFieldMeta(name).value;

  const isFormReference =
    Object.keys(formikObject ?? ({} as YupSchemaReferenceType)).includes('id') &&
    Object.keys(formikObject ?? ({} as YupSchemaReferenceType)).includes('option');

  // If the value has an 'id' field, consider that to be the main value used.
  const formikId = isFormReference ? (formikObject as YupSchemaReferenceType).id : formikObject;

  const [stateOpt, setStateOpt] = useState<LookupInputOption | null>(
    GetOptFromValue(formikId, options),
  );
  const [stateValue, setStateValue] = useState<string>(labelsByValue.get(formikId) ?? '');

  const groupBy = !grouped ? undefined : (opt: LookupInputOption): string => opt.group ?? '';

  // Ensure that the stateValue is in sync with the field value at all times.
  useEffect(() => {
    if (formikId === null) {
      setStateOpt(null);
      setStateValue('');
      return;
    }
    const match = labelsByValue.get(formikId);
    if (match !== undefined && stateValue !== match) {
      setStateOpt(GetOptFromValue(formikId, options));
      setStateValue(match);
    }
  }, [formikId, labelsByValue]);

  return (
    <Field id={name} name={name} validate={validate}>
      {({
        field: { onBlur },
        form: { setFieldTouched, setFieldValue },
        meta,
      }: FieldAttributes<any>): ReactElement => {
        const errorMessage =
          isFormReference && typeof meta.error === 'object' && meta.error !== null
            ? (meta.error as any).id
            : meta.error;
        return (
          <Autocomplete
            readOnly={readOnly}
            id={name}
            onBlur={onBlur}
            disabled={disabled}
            isOptionEqualToValue={(option, value): boolean =>
              value === undefined || option.value === value.value
            }
            inputValue={stateValue}
            onInputChange={(_, inputText): void => {
              setStateValue(inputText);
            }}
            value={stateOpt}
            onChange={(_, selectedOpt): void => {
              setStateOpt(selectedOpt);

              setFieldTouched(name);

              // Input has no text
              if (selectedOpt === null) {
                onMatchChange?.(null);
                setFieldValue(name, isFormReference ? { id: null, option: null } : null);
                return;
              }
              // Input has text that matches an LookupInputOption

              onMatchChange?.(selectedOpt.value);
              // Update the value if it is a reference, or a direct value.
              const newValue = isFormReference
                ? {
                    id: selectedOpt.value,
                    option: selectedOpt,
                  }
                : selectedOpt.value;
              setFieldValue(name, newValue, true);
            }}
            loading={isLoading}
            loadingText='Loading...'
            autoComplete={false}
            disablePortal
            fullWidth
            options={options}
            groupBy={groupBy}
            renderInput={(params): ReactElement => (
              <LookupInputInput
                hasError={meta.touched === true && meta.error !== undefined}
                helperText={errorMessage}
                isLoading={isLoading}
                name={name}
                params={params}
                stateValue={stateValue}
                label={label}
                placeholder={placeholder}
                tip={tip}
              />
            )}
            filterOptions={filterOptions}
            renderOption={LookupInputAutocompleteOption}
            sx={{
              ...sx,
              ...(readOnly ? { pointerEvents: 'none' } : {}),
            }}
          />
        );
      }}
    </Field>
  );
};
